import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { PARTNER_LISTING_BAND_ASPECT_CLASS } from '@/lib/constants/image-crop';
import { trustedPartnerDetailPath } from '@/lib/constants/routes';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { DirectoryPartnerCard } from '../api/trusted-partners.api';
import { PartnerCover } from './PartnerCover';
import { TRUSTED_PARTNERS_STRINGS as S } from '../trusted-partners.constants';

/**
 * One partner in the directory (FR-111). Deliberately carries no contact action:
 * the contact method lives on the profile page only (FR-109).
 */
export function PartnerCard({ partner }: { partner: DirectoryPartnerCard }) {
  return (
    // No overflow-hidden here: the logo disc overhangs the top edge (FR-293). The cover rounds its own top corners instead.
    <article className="relative flex min-w-0 flex-col rounded-card bg-white shadow-card">
      {/* FR-116: the listing cover, else the detail cover (centre-cropped), else the brand gradient. */}
      <PartnerCover src={partner.listingCoverUrl ?? partner.coverPhotoUrl} className={`${PARTNER_LISTING_BAND_ASPECT_CLASS} rounded-t-card`}>
        {partner.category && (
          <span className="absolute bottom-2 right-2 rounded bg-navy/70 px-2 py-0.5 text-[10px] font-medium text-white">
            {partner.category}
          </span>
        )}
      </PartnerCover>
      {/* FR-293: a share of the card's width (Figma 112 of 399 px), capped, rising by a third of its own height.
          A sibling of the cover, which would clip it. The wrapper is a size container so the initials scale with the disc,
          and carries a soft shadow because a white disc is otherwise invisible where it rises over the white section. */}
      <div className="@container absolute left-[10.8%] top-0 aspect-square w-[28%] max-w-24 -translate-y-1/3 rounded-full shadow-card-soft">
        {/* White disc with dark initials; `!` because Avatar sets bg-navy/text-white and an inline width/height that would otherwise win. */}
        <Avatar
          photoUrl={partner.logoUrl ?? undefined}
          initials={initialsFromName(partner.businessName)}
          className="size-full! bg-white! text-navy! text-[30cqw]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="break-words text-p5 font-semibold text-navy">{partner.businessName}</h3>
        {partner.tagline && <p className="break-words text-p6 text-slate">{partner.tagline}</p>}
        <div className="mt-auto flex items-center justify-between border-t border-divider pt-3">
          {partner.hasOffer ? (
            <span className="flex items-center gap-1 text-[11px] font-medium text-on-yellow">
              <Star size={12} className="fill-yellow text-yellow" aria-hidden="true" />
              {S.HAS_OFFER}
            </span>
          ) : (
            <span />
          )}
          <Button asChild variant="outline" className="px-3! py-1! text-xs">
            <Link to={trustedPartnerDetailPath(partner.entityId)}>{S.VIEW_PROFILE}</Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
